import { Carousel, pxToRem, useStyles } from '@archipro-design/aria';
import * as S from './FeaturedCarousel.style';

import type { FeaturedCarouselProps } from './FeaturedCarousel';
import { Link } from 'react-router';

import { useRef } from 'react';
import ArchizeenTile from '~/modules/tile/component/archizeen-tile/ArchizeenImpressionTile';
import FeaturedCarouselAdMobile from '@modules/articles/component/featured-carousel/FeaturedCarouselAd.mobile';

import { useLocalisation } from '@archipro-website/localisation';
import { getFormattedLocalisedDate } from '@modules/date-time/util/get-localised-date';

const FeaturedCarouselMobile: React.FC<
    FeaturedCarouselProps & {
        height?: number | null;
    }
> = ({ featuredSlides, imageLazyLoading, height = 475 }) => {
    const { css } = useStyles();

    const ref = useRef(null);

    const { localiseDate } = useLocalisation();

    const carouselItems = featuredSlides.map((article) => {
        const {
            ID,
            link,
            title,
            categoryLabel,
            date,
            readTime,
            image,
            professionalID,
            hasVideo,
            canEdit: _canEdit,
            bmEditorLink: _bmEditorLink,
            ad,
            ...rest
        } = article;

        if (ad) {
            return {
                key: ID,
                id: ID,
                content: (
                    <FeaturedCarouselAdMobile
                        featuredSlide={article}
                        ad={ad}
                        imageLazyLoading={imageLazyLoading}
                    />
                ),
            };
        } else {
            return {
                key: ID,
                id: ID,
                content: (
                    <ArchizeenTile
                        {...rest}
                        as={Link}
                        to={hasVideo ? `${link}?video=1` : link}
                        prefetch="intent"
                        className={css(S.MobileTile)}
                        size={'medium'}
                        variant="03"
                        articleId={ID}
                        variables={{
                            tilePadding: 0,
                        }}
                        image={{
                            ...image,
                            alt: title,
                            loading: imageLazyLoading,
                            sizes: '100vw',
                        }}
                        title={{
                            children: title,
                            maximumLines: 3,
                        }}
                        label={{
                            children: categoryLabel,
                            variant: '06',
                        }}
                        date={{
                            children: getFormattedLocalisedDate(
                                localiseDate,
                                date,
                            ),
                            variant: '06',
                        }}
                        readTime={{
                            children: readTime,
                            variant: '06',
                        }}
                        favouritePosition="card"
                        impression={{
                            itemID: ID,
                            type: 'LibraryItem_Directory',
                            professionalID: professionalID,
                        }}
                    />
                ),
                'aria-label': article.title,
            };
        }
    });

    return (
        <div className={css(S.Wrap)}>
            <Carousel
                ref={ref}
                items={carouselItems}
                hasNavigation={false}
                pagination={{
                    type: 'bullets',
                    clickable: true,
                }}
                loop={carouselItems.length > 1}
                paddlePosition="flex-end"
                variables={{
                    containerWidth: `100%`,
                    containerHeight: height ? pxToRem(height) : 'unset',
                    overlayPadding: pxToRem(0),
                    overlayInitialOpacity: 1,
                }}
                className={css(S.Carousel)}
                hidePaddles={true}
                autoplay={
                    carouselItems.length > 1
                        ? {
                              delay: 5000,
                              pauseOnMouseEnter: true,
                              disableOnInteraction: false,
                          }
                        : {}
                }
            />
        </div>
    );
};

export default FeaturedCarouselMobile;
